// ============================================================================
// INPUT SCHEMAS
// Per-algorithm input schema definitions and randomization helpers
// ============================================================================

export type FieldType =
  | 'number-array'
  | 'number'
  | 'string'
  | 'string-pair'
  | 'edge-list'
  | 'matrix'
  | 'word-list'
  | 'json';

export interface FieldSchema {
  key: string;
  label: string;
  type: FieldType;
  description?: string;
  min?: number;
  max?: number;
  minLen?: number;
  maxLen?: number;
}

export interface InputSchema {
  fields: FieldSchema[];
  defaults: Record<string, unknown>;
  /** Serialises field values into the `buildInputData`-compatible envelope */
  toInputEnvelope: (values: Record<string, unknown>) => unknown;
}

// ── helpers ─────────────────────────────────────────────────────────────────

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randArray(len: number, min = 1, max = 99): number[] {
  return Array.from({ length: len }, () => randInt(min, max));
}

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

// ── schema builders ──────────────────────────────────────────────────────────

function arraySchema(defaults: number[]): InputSchema {
  return {
    fields: [{ key: 'values', label: 'Array', type: 'number-array', description: 'Comma-separated numbers' }],
    defaults: { values: defaults },
    toInputEnvelope: (v) => ({ type: 'array', data: v['values'] }),
  };
}

function searchSchema(defaults: number[], target: number): InputSchema {
  return {
    fields: [
      { key: 'values', label: 'Sorted array', type: 'number-array', description: 'Numbers to search in' },
      { key: 'target', label: 'Target value', type: 'number', min: 0, max: 9999 },
    ],
    defaults: { values: defaults, target },
    toInputEnvelope: (v) => ({ type: 'array', data: v['values'], target: v['target'] }),
  };
}

function graphSchema(directed: boolean, weighted: boolean, defaultData: object): InputSchema {
  return {
    fields: [
      { key: 'startNode', label: 'Start node', type: 'string' },
      { key: 'edges', label: 'Edge list (JSON)', type: 'edge-list', description: 'e.g. [{"from":"A","to":"B","weight":3}]' },
    ],
    defaults: defaultData as Record<string, unknown>,
    toInputEnvelope: (v) => ({
      type: 'graph',
      data: { ...(v as object), directed, weighted },
    }),
  };
}

function stringPairSchema(text: string, pattern: string): InputSchema {
  return {
    fields: [
      { key: 'text', label: 'Text', type: 'string' },
      { key: 'pattern', label: 'Pattern', type: 'string' },
    ],
    defaults: { text, pattern },
    toInputEnvelope: (v) => ({ type: 'generic', data: v }),
  };
}

function singleStringSchema(text: string): InputSchema {
  return {
    fields: [{ key: 'text', label: 'Text', type: 'string' }],
    defaults: { text },
    toInputEnvelope: (v) => ({ type: 'generic', data: v }),
  };
}

function twoStringSchema(word1: string, word2: string): InputSchema {
  return {
    fields: [
      { key: 'word1', label: 'Word 1', type: 'string' },
      { key: 'word2', label: 'Word 2', type: 'string' },
    ],
    defaults: { word1, word2 },
    toInputEnvelope: (v) => ({ type: 'string', data: v }),
  };
}

function singleNumberSchema(key: string, label: string, defaultVal: number, min = 1, max = 100): InputSchema {
  return {
    fields: [{ key, label, type: 'number', min, max }],
    defaults: { [key]: defaultVal },
    toInputEnvelope: (v) => ({ type: 'generic', data: v }),
  };
}

function dsTreeSchema(defaults: number[]): InputSchema {
  return {
    fields: [{ key: 'values', label: 'Values', type: 'number-array', description: 'Insertion order for the tree' }],
    defaults: { values: defaults },
    toInputEnvelope: (v) => ({ type: 'array', data: v['values'] }),
  };
}

// ── category / algorithm default graph data ───────────────────────────────────

const TRAVERSAL_GRAPH = {
  startNode: 'A',
  vertices: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'],
  edges: [
    { from: 'A', to: 'B', weight: 1 }, { from: 'A', to: 'C', weight: 1 },
    { from: 'B', to: 'D', weight: 1 }, { from: 'B', to: 'E', weight: 1 },
    { from: 'C', to: 'F', weight: 1 }, { from: 'C', to: 'G', weight: 1 },
    { from: 'D', to: 'H', weight: 1 }, { from: 'E', to: 'H', weight: 1 },
  ],
};

const SP_GRAPH = {
  startNode: 'S',
  vertices: ['S', 'A', 'B', 'C', 'D', 'E', 'F', 'T'],
  edges: [
    { from: 'S', to: 'A', weight: 7 }, { from: 'S', to: 'B', weight: 3 },
    { from: 'A', to: 'C', weight: 2 }, { from: 'B', to: 'C', weight: 1 },
    { from: 'B', to: 'D', weight: 5 }, { from: 'C', to: 'D', weight: 3 },
    { from: 'C', to: 'E', weight: 4 }, { from: 'D', to: 'E', weight: 1 },
    { from: 'D', to: 'T', weight: 2 }, { from: 'E', to: 'T', weight: 6 },
  ],
};

// ── main export ───────────────────────────────────────────────────────────────

/**
 * Returns an InputSchema for a given algorithm based on its id and category.
 * Falls back to a generic JSON editor if no schema is known.
 */
export function getInputSchema(algorithmId: string, category: string): InputSchema {
  const id = algorithmId.toLowerCase();
  const cat = category.toLowerCase();

  // ── sorting ────────────────────────────────────────────────────────────────
  if (cat === 'sorting') {
    return arraySchema([64, 34, 25, 12, 22, 11, 90, 45, 50, 88]);
  }

  // ── search ─────────────────────────────────────────────────────────────────
  if (cat === 'search') {
    return searchSchema([2, 5, 8, 12, 16, 23, 38, 56, 72, 91, 105, 120, 145, 178, 200], 72);
  }

  // ── graph ──────────────────────────────────────────────────────────────────
  if (cat === 'graph') {
    // Range Tree uses 2D points, not edge-list
    if (id.includes('range-tree')) {
      return {
        fields: [
          { key: 'points', label: 'Points (JSON)', type: 'json', description: '[{"x":0,"y":0}, ...]' },
          { key: 'query', label: 'Query range (JSON)', type: 'json', description: '{"x_min":0,"x_max":100,"y_min":0,"y_max":100}' },
        ],
        defaults: {
          points: [
            { x: 2, y: 3 }, { x: 5, y: 4 }, { x: 9, y: 6 },
            { x: 4, y: 7 }, { x: 8, y: 1 }, { x: 7, y: 2 },
          ],
          query: { x_min: 3, x_max: 8, y_min: 0, y_max: 5 },
        },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    }
    const directed =
      id.includes('topological') || id.includes('scc') ||
      id.includes('flow') || id.includes('dinic') ||
      id.includes('ford') || id.includes('edmonds');
    const isSP =
      id.includes('dijkstra') || id.includes('bellman') ||
      id.includes('astar') || id.includes('floyd');
    const isTraversal = id.includes('bfs') || id.includes('dfs') || id.includes('connected');
    const base = isSP ? SP_GRAPH : isTraversal ? TRAVERSAL_GRAPH : SP_GRAPH;
    return graphSchema(directed, true, base);
  }

  // ── string ─────────────────────────────────────────────────────────────────
  if (cat === 'string') {
    if (id.includes('kmp') || id.includes('rabin') || id.includes('boyer'))
      return stringPairSchema('ABABDABACDABABCABAB', 'ABABCABAB');
    if (id.includes('z-algo') || id.includes('manacher') || id.includes('rolling'))
      return singleStringSchema('abacabadabacabae');
    if (id.includes('suffix') || id.includes('aho') || id.includes('kasai'))
      return singleStringSchema('banana');
    if (id.includes('lcs') || id.includes('edit') || id.includes('levenshtein'))
      return twoStringSchema('kitten', 'sitting');
    return twoStringSchema('algorithm', 'altruistic');
  }

  // ── dp ─────────────────────────────────────────────────────────────────────
  if (cat === 'dp' || cat.includes('dynamic')) {
    if (id.includes('knapsack'))
      return {
        fields: [
          { key: 'weights', label: 'Weights', type: 'number-array' },
          { key: 'values', label: 'Values', type: 'number-array' },
          { key: 'capacity', label: 'Capacity', type: 'number', min: 1, max: 50 },
        ],
        defaults: { weights: [2, 3, 4, 5, 6], values: [3, 4, 5, 6, 7], capacity: 8 },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('coin'))
      return {
        fields: [
          { key: 'coins', label: 'Coin denominations', type: 'number-array' },
          { key: 'amount', label: 'Target amount', type: 'number', min: 1, max: 100 },
        ],
        defaults: { coins: [1, 2, 5], amount: 8 },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('lcs'))
      return {
        fields: [
          { key: 'text1', label: 'Text 1', type: 'string' },
          { key: 'text2', label: 'Text 2', type: 'string' },
        ],
        defaults: { text1: 'ABCBDAB', text2: 'BDCABA' },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('edit') || id.includes('levenshtein'))
      return {
        fields: [
          { key: 'word1', label: 'Word 1', type: 'string' },
          { key: 'word2', label: 'Word 2', type: 'string' },
        ],
        defaults: { word1: 'kitten', word2: 'sitting' },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('lis'))
      return {
        fields: [{ key: 'arr', label: 'Array', type: 'number-array' }],
        defaults: { arr: [10, 9, 2, 5, 3, 7, 101, 18] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('fibonacci'))
      return singleNumberSchema('n', 'n (compute fib(n))', 10, 1, 40);
    if (id.includes('matrix-chain'))
      return {
        fields: [{ key: 'dimensions', label: 'Dimensions', type: 'number-array' }],
        defaults: { dimensions: [5, 10, 3, 12, 5, 50, 6] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('word-break'))
      return {
        fields: [
          { key: 's', label: 'String', type: 'string' },
          { key: 'wordDict', label: 'Word dictionary', type: 'word-list', description: 'Comma-separated words' },
        ],
        defaults: { s: 'leetcode', wordDict: ['leet', 'code'] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('palindrom'))
      return {
        fields: [{ key: 's', label: 'String', type: 'string' }],
        defaults: { s: 'bbbab' },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    return arraySchema([10, 9, 2, 5, 3, 7, 101, 18]);
  }

  // ── math ───────────────────────────────────────────────────────────────────
  if (cat === 'math') {
    if (id.includes('sieve')) return singleNumberSchema('n', 'Find primes up to n', 30, 2, 200);
    if (id.includes('fast-exp') || id.includes('fast_exp'))
      return {
        fields: [
          { key: 'base', label: 'Base', type: 'number', min: 1, max: 100 },
          { key: 'exponent', label: 'Exponent', type: 'number', min: 1, max: 30 },
          { key: 'mod', label: 'Modulus', type: 'number', min: 2, max: 1000 },
        ],
        defaults: { base: 3, exponent: 13, mod: 17 },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('gcd'))
      return {
        fields: [
          { key: 'a', label: 'a', type: 'number', min: 1, max: 10000 },
          { key: 'b', label: 'b', type: 'number', min: 1, max: 10000 },
        ],
        defaults: { a: 240, b: 46 },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('miller'))
      return singleNumberSchema('n', 'Number to test primality', 53, 2, 9999);
    if (id.includes('chinese'))
      return {
        fields: [
          { key: 'remainders', label: 'Remainders', type: 'number-array' },
          { key: 'moduli', label: 'Moduli', type: 'number-array' },
        ],
        defaults: { remainders: [2, 3, 2], moduli: [3, 5, 7] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    return singleNumberSchema('n', 'n', 20, 1, 200);
  }

  // ── backtracking ───────────────────────────────────────────────────────────
  if (cat === 'backtracking') {
    if (id.includes('queen') || id.includes('queens'))
      return singleNumberSchema('n', 'Board size n×n', 4, 1, 8);
    if (id.includes('sudoku'))
      return {
        fields: [{ key: 'board', label: 'Board (9×9 JSON)', type: 'matrix' }],
        defaults: {
          board: [
            [5,3,0,0,7,0,0,0,0],[6,0,0,1,9,5,0,0,0],[0,9,8,0,0,0,0,6,0],
            [8,0,0,0,6,0,0,0,3],[4,0,0,8,0,3,0,0,1],[7,0,0,0,2,0,0,0,6],
            [0,6,0,0,0,0,2,8,0],[0,0,0,4,1,9,0,0,5],[0,0,0,0,8,0,0,7,9],
          ],
        },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('knights')) return singleNumberSchema('n', 'Board size n', 5, 3, 8);
    if (id.includes('rat'))
      return {
        fields: [{ key: 'maze', label: 'Maze (JSON 2D array)', type: 'matrix' }],
        defaults: { maze: [[1,0,0,0],[1,1,0,1],[0,1,0,0],[1,1,1,1]] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('hamiltonian'))
      return {
        fields: [{ key: 'graph', label: 'Adjacency matrix (JSON 2D array)', type: 'matrix' }],
        defaults: { graph: [[0,1,1,1,0],[1,0,1,0,1],[1,1,0,1,1],[1,0,1,0,1],[0,1,1,1,0]] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    return singleNumberSchema('n', 'n', 4, 1, 8);
  }

  // ── geometry ───────────────────────────────────────────────────────────────
  if (cat === 'geometry') {
    if (id.includes('line-intersection') || id.includes('line-segment')) {
      return {
        fields: [
          { key: 'line1', label: 'Line 1 (JSON)', type: 'json', description: '{"p1":{"x":0,"y":0},"p2":{"x":4,"y":4}}' },
          { key: 'line2', label: 'Line 2 (JSON)', type: 'json', description: '{"p1":{"x":0,"y":4},"p2":{"x":4,"y":0}}' },
        ],
        defaults: {
          line1: { p1: { x: 0, y: 0 }, p2: { x: 4, y: 4 } },
          line2: { p1: { x: 0, y: 4 }, p2: { x: 4, y: 0 } },
        },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    }
    return {
      fields: [{ key: 'points', label: 'Points (JSON)', type: 'json', description: '[{"x":0,"y":0}, ...]' }],
      defaults: {
        points: [
          { id: 1, x: 0, y: 0 }, { id: 2, x: 1, y: 3 },
          { id: 3, x: 3, y: 1 }, { id: 4, x: 3, y: 3 },
          { id: 5, x: 5, y: 0 }, { id: 6, x: 2, y: 2 },
        ],
      },
      toInputEnvelope: (v) => ({ type: 'generic', data: v }),
    };
  }

  // ── data structures ────────────────────────────────────────────────────────
  if (cat === 'data structures' || cat === 'data-structures') {
    if (id.includes('trie'))
      return {
        fields: [
          { key: 'words', label: 'Words', type: 'word-list', description: 'Comma-separated words to insert' },
          { key: 'target', label: 'Search for', type: 'string' },
        ],
        defaults: { words: ['cat', 'car', 'dog', 'door', 'card'], target: 'car' },
        toInputEnvelope: (v) => ({ type: 'generic', data: { ...(v as object), operation: 'search' } }),
      };
    if (id.includes('union') || id.includes('disjoint'))
      return {
        fields: [
          { key: 'size', label: 'Set size', type: 'number', min: 2, max: 20 },
        ],
        defaults: {
          size: 8,
          operations: [
            { type: 'union', params: [0, 1] }, { type: 'union', params: [2, 3] },
            { type: 'union', params: [1, 3] }, { type: 'find', params: [0] },
          ],
        },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('rope'))
      return {
        fields: [
          { key: 'text', label: 'Initial string', type: 'string' },
          { key: 'operation', label: 'Operation', type: 'string', description: "'insert' or 'split'" },
        ],
        defaults: { text: 'HelloWorld', operation: 'insert', params: [5, 'Beautiful'] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('segment'))
      return {
        fields: [
          { key: 'values', label: 'Array values', type: 'number-array' },
          { key: 'queryL', label: 'Query L', type: 'number', min: 0, max: 99 },
          { key: 'queryR', label: 'Query R', type: 'number', min: 0, max: 99 },
        ],
        defaults: { values: [1, 3, 5, 7, 9, 11], queryL: 1, queryR: 4 },
        toInputEnvelope: (v) => {
          const { values, queryL, queryR, ...rest } = v as Record<string, unknown>;
          return { type: 'array', data: values, query: [queryL, queryR], ...rest };
        },
      };
    if (id.includes('fenwick'))
      return {
        fields: [
          { key: 'values', label: 'Array values', type: 'number-array' },
          { key: 'queryIndex', label: 'Query prefix index', type: 'number', min: 0, max: 99 },
        ],
        defaults: { values: [1, 3, 5, 7, 9, 11], queryIndex: 3 },
        toInputEnvelope: (v) => {
          const { values, queryIndex } = v as Record<string, unknown>;
          return { type: 'array', data: values, queryIndex };
        },
      };
    if (id.includes('skip'))
      return {
        fields: [
          { key: 'values', label: 'Values', type: 'number-array' },
          { key: 'target', label: 'Search target', type: 'number' },
        ],
        defaults: { values: [3, 6, 7, 9, 12, 17, 19, 21, 25], target: 17 },
        toInputEnvelope: (v) => ({ type: 'array', data: (v as Record<string, unknown>)['values'], target: (v as Record<string, unknown>)['target'] }),
      };
    if (id.includes('kd'))
      return {
        fields: [{ key: 'points', label: 'Points (JSON)', type: 'json', description: '[[x, y], ...]' }],
        defaults: { points: [[30, 40], [5, 25], [70, 70], [10, 12], [50, 30], [35, 45]] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('binary-lift'))
      return {
        fields: [
          { key: 'edges', label: 'Edges (JSON)', type: 'json', description: '[[u, v], ...]' },
          { key: 'queries', label: 'Queries (JSON)', type: 'json', description: '[[u, v], ...]' },
        ],
        defaults: { edges: [[0,1],[0,2],[1,3],[1,4],[2,5],[2,6]], queries: [[3,4],[5,6],[3,6],[4,5]] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('range-tree-ds'))
      return {
        fields: [
          { key: 'points', label: 'Points (JSON)', type: 'json', description: '[{"id":1,"x":0,"y":0}, ...]' },
          { key: 'queries', label: 'Queries (JSON)', type: 'json', description: '[{"x_min":0,"x_max":3,"y_min":0,"y_max":3}, ...]' },
        ],
        defaults: { points: [{ id: 1, x: 0, y: 0 }, { id: 2, x: 1, y: 3 }, { id: 3, x: 3, y: 1 }, { id: 4, x: 3, y: 3 }, { id: 5, x: 5, y: 0 }, { id: 6, x: 2, y: 2 }], queries: [{ x_min: 0, x_max: 3, y_min: 0, y_max: 3 }] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('binomial'))
      return {
        fields: [{ key: 'keys', label: 'Keys to insert', type: 'number-array' }],
        defaults: { keys: [5, 10, 3, 8, 1, 7, 12, 4, 9, 2] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    return dsTreeSchema([10, 5, 15, 2, 7, 12, 20]);
  }

  // ── tree (structural) ──────────────────────────────────────────────────────
  if (cat === 'tree') {
    return dsTreeSchema([10, 5, 15, 2, 7, 12, 20]);
  }

  // ── hybrid (sorting variants) ─────────────────────────────────────────────
  if (cat === 'hybrid') {
    return arraySchema([64, 34, 25, 12, 22, 11, 90, 88, 45, 50]);
  }

  // ── range tree (data structure) ────────────────────────────────────────────
  if (cat === 'range tree') {
    if (id.includes('range-tree-ds')) {
      return {
        fields: [
          { key: 'points', label: 'Points (JSON)', type: 'json', description: '[{id,x,y}, ...]' },
          { key: 'queries', label: 'Queries (JSON)', type: 'json', description: '[{x_min,x_max,y_min,y_max}, ...]' },
        ],
        defaults: {
          points: [{ id: 1, x: 0, y: 0 }, { id: 2, x: 1, y: 3 }, { id: 3, x: 3, y: 1 }, { id: 4, x: 3, y: 3 }, { id: 5, x: 5, y: 0 }, { id: 6, x: 2, y: 2 }],
          queries: [{ x_min: 0, x_max: 3, y_min: 0, y_max: 3 }],
        },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    }
  }

  // ── greedy ─────────────────────────────────────────────────────────────────
  if (cat === 'greedy') {
    if (id.includes('huffman'))
      return {
        fields: [{ key: 'text', label: 'Text to encode', type: 'string' }],
        defaults: { text: 'abracadabra' },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('fractional'))
      return {
        fields: [{ key: 'items', label: 'Items (JSON)', type: 'json', description: '[{id,weight,value}, ...]' },
                 { key: 'capacity', label: 'Capacity', type: 'number', min: 1, max: 999 }],
        defaults: { items: [{ id: 'A', weight: 10, value: 60 }, { id: 'B', weight: 20, value: 100 }, { id: 'C', weight: 30, value: 120 }], capacity: 50 },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('job'))
      return {
        fields: [{ key: 'jobs', label: 'Jobs (JSON)', type: 'json', description: '[{id,deadline,profit}, ...]' }],
        defaults: { jobs: [{ id: 'J1', deadline: 2, profit: 100 }, { id: 'J2', deadline: 1, profit: 19 }, { id: 'J3', deadline: 2, profit: 27 }, { id: 'J4', deadline: 1, profit: 25 }, { id: 'J5', deadline: 3, profit: 15 }] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    if (id.includes('activity'))
      return {
        fields: [{ key: 'activities', label: 'Activities (JSON)', type: 'json', description: '[{name,start,finish}, ...]' }],
        defaults: { activities: [{ name: 'A1', start: 1, finish: 4 }, { name: 'A2', start: 3, finish: 5 }, { name: 'A3', start: 0, finish: 6 }, { name: 'A4', start: 5, finish: 7 }, { name: 'A5', start: 3, finish: 9 }, { name: 'A6', start: 5, finish: 9 }, { name: 'A7', start: 6, finish: 10 }, { name: 'A8', start: 8, finish: 11 }] },
        toInputEnvelope: (v) => ({ type: 'generic', data: v }),
      };
    return {
      fields: [{ key: 'data', label: 'Input (JSON)', type: 'json' }],
      defaults: { data: {} },
      toInputEnvelope: (v) => ({ type: 'generic', data: (v as Record<string, unknown>)['data'] }),
    };
  }

  // fallback
  return {
    fields: [{ key: 'data', label: 'Input (JSON)', type: 'json' }],
    defaults: { data: {} },
    toInputEnvelope: (v) => ({ type: 'generic', data: (v as Record<string, unknown>)['data'] }),
  };
}

// ── randomize ─────────────────────────────────────────────────────────────────

/**
 * Returns a randomised copy of the field values for a given schema.
 * Falls back to the schema defaults for fields that cannot be randomised.
 */
export function randomizeValues(schema: InputSchema): Record<string, unknown> {
  const result: Record<string, unknown> = { ...schema.defaults };
  for (const field of schema.fields) {
    switch (field.type) {
      case 'number-array': {
        const len = randInt(field.minLen ?? 6, field.maxLen ?? 12);
        result[field.key] = randArray(len, field.min ?? 1, field.max ?? 99);
        break;
      }
      case 'number': {
        const lo = field.min ?? 1;
        const hi = field.max ?? 100;
        result[field.key] = randInt(lo, hi);
        break;
      }
      case 'string': {
        const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.toLowerCase();
        const len = randInt(field.minLen ?? 4, field.maxLen ?? 10);
        result[field.key] = Array.from({ length: len }, () => alphabet[randInt(0, 25)]).join('');
        break;
      }
      case 'string-pair': {
        const alphabet = 'ABCD';
        const len = randInt(6, 14);
        result['text'] = Array.from({ length: len }, () => alphabet[randInt(0, 3)]).join('');
        result['pattern'] = Array.from({ length: randInt(3, 6) }, () => alphabet[randInt(0, 3)]).join('');
        break;
      }
      case 'word-list': {
        const pool = ['cat', 'car', 'card', 'care', 'dog', 'door', 'do', 'bat', 'ban', 'bad'];
        result[field.key] = shuffled(pool).slice(0, randInt(4, 7));
        break;
      }
      case 'json': {
        // Randomize 2D points for KD-tree / geometry algorithms
        if (field.key === 'points' || field.key === 'pointsJson') {
          const n = randInt(5, 10);
          const useObjects = Array.isArray(schema.defaults[field.key]) &&
            typeof (schema.defaults[field.key] as unknown[])[0] === 'object';
          if (useObjects) {
            result[field.key] = Array.from({ length: n }, (_, i) => ({
              id: i,
              x: randInt(0, 100),
              y: randInt(0, 100),
            }));
          } else {
            result[field.key] = Array.from({ length: n }, () => [randInt(0, 100), randInt(0, 100)]);
          }
        }
        break;
      }
      case 'edge-list': {
        // Generate a random connected graph
        const n = randInt(5, 9);
        const vertices = Array.from({ length: n }, (_, i) => String.fromCharCode(65 + i));
        const edges: { from: string; to: string; weight: number }[] = [];
        for (let i = 1; i < n; i++) {
          const from = vertices[randInt(0, i - 1)]!;
          const to = vertices[i]!;
          edges.push({ from, to, weight: randInt(1, 15) });
        }
        const extra = randInt(2, Math.min(n, 5));
        for (let i = 0; i < extra; i++) {
          const a = vertices[randInt(0, n - 1)]!;
          const b = vertices[randInt(0, n - 1)]!;
          if (a !== b) edges.push({ from: a, to: b, weight: randInt(1, 15) });
        }
        result[field.key] = edges;
        if ('startNode' in schema.defaults) result['startNode'] = vertices[0];
        if ('vertices' in schema.defaults) result['vertices'] = vertices;
        break;
      }
      case 'matrix': {
        // Randomize matrix fields (e.g. sudoku, maze)
        if (field.key === 'maze') {
          const size = randInt(4, 7);
          const maze: number[][] = Array.from({ length: size }, () =>
            Array.from({ length: size }, () => (Math.random() > 0.3 ? 1 : 0))
          );
          maze[0]![0] = 1;
          maze[size - 1]![size - 1] = 1;
          result[field.key] = maze;
        }
        break;
      }
      default:
        break;
    }
  }
  return result;
}
